// "Higher intelligence" — Memory, Context, Files. Framed generically about
// Companion's capabilities (Otto does the routing behind the scenes; not
// surfaced here). Clean cards — no mascot avatars, no scenarios.
import { CtaPair } from "../components/CtaPair";
import { TreatIcon } from "../components/TreatIcon";

type Skill = {
  key: string;
  kicker: string;
  accent: string;
  tint: string;
  headline: string; // may contain \n for line break
  body: string;
};

const SKILLS: Skill[] = [
  {
    key: "memory",
    kicker: "Memory",
    accent: "#F59E0B",
    tint: "#FEF3C7",
    headline: "Remembers your preferences. Permanently.",
    body: "Tell Companion your preferences once — like how you structure your SOAPs or where you want differentials listed — and it follows them from then on.",
  },
  {
    key: "context",
    kicker: "Context",
    accent: "#EC4899",
    tint: "#FCE7F3",
    headline: "Six months of notes.\nOne question.",
    body: "Companion reads every note you've made in Scribenote — so when you ask about a patient, it already knows what happened last visit, what was tried, and what to look out for next.",
  },
  {
    key: "files",
    kicker: "Files",
    accent: "#10B981",
    tint: "#D1FAE5",
    headline: "Upload a file.\nGet Companion's take.",
    body: "Drop in any PDF or photo from the exam room. Companion will help you make sense of it — flag what matters, summarize the findings, and answer follow-ups right in the conversation.",
  },
];

function SkillCard({ s, idx }: { s: Skill; idx: number }) {
  return (
    <div
      style={
        {
          "--accent": s.accent,
          "--tint": s.tint,
        } as React.CSSProperties
      }
      className="tilt-card relative flex flex-col gap-4 overflow-hidden rounded-[24px] border border-grindigo-100 bg-white p-7 shadow-[0_20px_40px_-20px_rgba(99,102,241,0.15)] transition-[transform,box-shadow,border-color] duration-[400ms] [transition-timing-function:cubic-bezier(0.2,0.9,0.3,1)]"
    >
      {/* corner gradient flourish */}
      <div className="pointer-events-none absolute -top-[60px] -right-[60px] h-[200px] w-[200px] rounded-full opacity-85 bg-[radial-gradient(circle,var(--tint)_0%,transparent_70%)]" />

      <div className="relative z-[1] flex items-center gap-2.5">
        <span className="font-companion tracking-[-0.01em] grid h-7 w-7 place-items-center rounded-full bg-[var(--tint)] text-[13px] font-bold text-[var(--accent)]">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <span className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--accent)]">
          {s.kicker}
        </span>
      </div>

      <h3 className="font-companion tracking-[-0.01em] relative z-[1] m-0 whitespace-pre-line text-[22px] font-bold leading-[1.15] tracking-[-0.015em] text-primary">
        {s.headline}
      </h3>
      <p className="relative z-[1] m-0 text-[15px] leading-[1.6] text-secondary [text-wrap:pretty]">
        {s.body}
      </p>
    </div>
  );
}

export function HigherIntelligence() {
  return (
    <section
      id="higher-intelligence"
      className="bg-[linear-gradient(180deg,#FAFAFF_0%,#FFFFFF_100%)] px-[clamp(24px,5vw,80px)] py-24"
    >
      <div className="mx-auto max-w-[1240px]">
        <div className="mb-12 max-w-[720px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.04em] text-indigo-700">
            Higher intelligence
          </div>
          <h2 className="font-companion tracking-[-0.01em] mt-0 mb-5 text-[clamp(32px,4vw,48px)] font-bold leading-[1.1] tracking-[-0.025em] text-primary">
            Adapts to you, not the other way around.
          </h2>
          <p className="mt-5 text-lg leading-[1.55] text-secondary [text-wrap:pretty]">
            Three things your veterinary software has never done before.
          </p>
        </div>

        <div className="cards-grid grid grid-cols-3 gap-6">
          {SKILLS.map((s, i) => (
            <SkillCard key={s.key} s={s} idx={i} />
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center gap-4">
          <CtaPair tone="light" size="md" align="center" />
          <div className="max-w-[420px] px-4 text-center text-sm text-secondary">
            <span className="mr-2 inline-block align-middle">
              <TreatIcon size={20} />
            </span>
            Get <b className="text-primary">500 free Treats</b> when you get
            started with Companion.
          </div>
        </div>
      </div>
    </section>
  );
}
